import {
    PersonalProductRecommendationBuilder,
    PopularProductsBuilder,
    ProductsViewedAfterViewingProductBuilder,
    PurchasedWithProductBuilder,
    RecentlyViewedProductsBuilder,
    Recommender,
    SimilarProductsProductBuilder,
    ValueSelectorFactory
} from '@relewise/client';
import { RELEWISE_MINIMUM_STOCK_COUNT } from './const';
import { relewiseMappingService } from './MappingService';
import { RelewiseProductResult } from './types';
import { relewiseUserService } from './UserService';
import { getRelewiseCurrencyId } from './utils';

const POPULARITY_TIME_WINDOW = 10080; // 7 days in minutes
const DEFAULT_RELEVANCE_MODIFIER_STRENGTH = 20;
const BY_PRODUCT_RECOMMENDATION_TYPES = ['similar', 'purchasedWith', 'viewedAfter'];

export type ProductRecommendationTypes =
    | 'mostViewed'
    | 'mostPurchased'
    | 'recentlyViewed'
    | 'personalized'
    | 'purchasedWith'
    | 'similar'
    | 'viewedAfter';
export type RelevanceModifier = 'boostDiscounted' | 'none';

interface GenericProductRecommendationParams {
    recommendationType: ProductRecommendationTypes;
    count: number;
    displayLocation: string;
    categoryId?: string;
    masterProductItemId?: string;
    relevanceModifier?: RelevanceModifier;
    relevanceModifierStrength?: number;
}

interface InitializedService {
    recommender: Recommender;
    currencyId: string;
    disabled: false;
    locale: string;
    channelId: string;
    environmentName: string;
}

class RelewiseRecommendationService {
    protected recommender: Recommender | undefined;
    protected currencyId: string | undefined;
    protected locale: string | undefined;
    protected channelId: string | undefined;
    protected environmentName: string | undefined;
    protected disabled = true;

    constructor() {
        // Recommendation service will run only on client side
        if (typeof window === 'undefined') {
            return;
        }

        const { relewiseApiKey, relewiseDatasetId, relewiseUrl, environmentName } =
            window.___initialData___?.requestContext.app.config ?? {};
        this.environmentName = environmentName;
        if (!relewiseApiKey || !relewiseDatasetId || !relewiseUrl || !this.environmentName) {
            console.error(
                '[Relewise Tracking Service] Cannot initialize service: Missing Relewise configuration. Expected relewiseApiKey, relewiseDatasetId, relewiseUrl, and environmentName. Add this config in the CMS.'
            );
            return;
        }

        this.channelId = window.___initialData___?.requestContext.channel?.RecordId?.toString();
        if (!this.channelId) {
            console.error('[Relewise Recommendation Service] Cannot initialize service: Channel ID is not configured.');
            return;
        }

        this.currencyId = getRelewiseCurrencyId();
        if (!this.currencyId) {
            console.error('[Relewise Recommendation Service] Cannot initialize service: Currency is not configured.');
            return;
        }

        this.locale = window.___initialData___?.requestContext.locale;
        if (!this.locale) {
            console.error('[Relewise Recommendation Service] Cannot initialize service: Locale is not configured.');
            return;
        }

        this.recommender = new Recommender(relewiseDatasetId, relewiseApiKey, {
            serverUrl: relewiseUrl
        });

        this.disabled = false;
    }

    private isEnabled(): this is InitializedService {
        return !this.disabled;
    }

    private checkIfByProductRecommendationType(recommendationType: ProductRecommendationTypes) {
        return BY_PRODUCT_RECOMMENDATION_TYPES.includes(recommendationType);
    }

    public async getGenericProductRecommendations({
        recommendationType,
        count,
        displayLocation,
        categoryId,
        masterProductItemId,
        relevanceModifier,
        relevanceModifierStrength
    }: GenericProductRecommendationParams): Promise<RelewiseProductResult[] | undefined> {
        if (!this.isEnabled()) {
            return [];
        }
        const isByProductRecommendation = this.checkIfByProductRecommendationType(recommendationType);
        if (isByProductRecommendation && !masterProductItemId) {
            return [];
        }

        try {
            const user = await relewiseUserService.getUser();

            const settings = {
                language: this.locale,
                currency: this.currencyId,
                displayedAtLocation: displayLocation,
                user
            };

            let builder;
            if (recommendationType === 'personalized') {
                builder = new PersonalProductRecommendationBuilder(settings);
            }
            if (recommendationType === 'mostPurchased') {
                builder = new PopularProductsBuilder(settings).basedOn('MostPurchased').sinceMinutesAgo(POPULARITY_TIME_WINDOW);
            }
            if (recommendationType === 'mostViewed') {
                builder = new PopularProductsBuilder(settings).basedOn('MostViewed').sinceMinutesAgo(POPULARITY_TIME_WINDOW);
            }
            if (recommendationType === 'recentlyViewed') {
                builder = new RecentlyViewedProductsBuilder(settings);
            }
            if (isByProductRecommendation && masterProductItemId) {
                if (recommendationType === 'similar') {
                    builder = new SimilarProductsProductBuilder(settings).product({
                        productId: relewiseMappingService.prefixId(masterProductItemId)
                    });
                }
                if (recommendationType === 'purchasedWith') {
                    builder = new PurchasedWithProductBuilder(settings).product({
                        productId: relewiseMappingService.prefixId(masterProductItemId)
                    });
                }
                if (recommendationType === 'viewedAfter') {
                    builder = new ProductsViewedAfterViewingProductBuilder(settings).product({
                        productId: relewiseMappingService.prefixId(masterProductItemId)
                    });
                }
            }

            if (!builder) {
                console.error(`[Relewise Recommendation Service] Unsupported recommendation type: ${recommendationType}`);
                return [];
            }

            builder
                .setNumberOfRecommendations(count)
                .allowFillIfNecessaryToReachNumberOfRecommendations(true)
                .allowProductsCurrentlyInCart(false)
                .setSelectedProductProperties({
                    displayName: true,
                    pricing: true,
                    allData: true
                })
                .setSelectedVariantProperties({
                    displayName: true,
                    pricing: true,
                    allSpecifications: true,
                    allData: true
                })
                .filters(f => {
                    f.addProductDataFilter(
                        'Environment',
                        b => b.addEqualsCondition({ value: this.environmentName, type: 'String', isCollection: false }),
                        true,
                        true
                    );
                    if (categoryId) {
                        f.addProductCategoryIdFilter('Ancestor', relewiseMappingService.prefixId(categoryId));
                    }
                    f.addProductDataFilter(
                        `HasVariantInStock_${this.channelId}`,
                        b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                        true,
                        true
                    );
                    f.addVariantDataFilter(
                        `StockCount_${this.channelId}`,
                        b => b.addGreaterThanCondition(RELEWISE_MINIMUM_STOCK_COUNT),
                        true,
                        true
                    );
                });

            if (relevanceModifier === 'boostDiscounted') {
                const cappedRelevanceModifierStrength = Math.min(
                    Math.max(relevanceModifierStrength ?? DEFAULT_RELEVANCE_MODIFIER_STRENGTH, 10),
                    100
                );
                const adjustedModifierStrength = cappedRelevanceModifierStrength / 10;
                builder.relevanceModifiers(m => {
                    m.addProductDataRelevanceModifier(
                        `HasDiscountedVariant_${this.channelId}`,
                        b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
                        ValueSelectorFactory.fixedDoubleValueSelector(adjustedModifierStrength),
                        true,
                        false
                    );
                    m.addVariantDataRelevanceModifier(
                        `IsDiscounted_${this.channelId}`,
                        b => {
                            b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false });
                        },
                        ValueSelectorFactory.fixedDoubleValueSelector(adjustedModifierStrength),
                        true,
                        false
                    );
                });
            }

            let result;
            if (builder instanceof PersonalProductRecommendationBuilder) {
                result = await this.recommender.recommendPersonalProducts(builder.build());
            }
            if (builder instanceof PopularProductsBuilder) {
                result = await this.recommender.recommendPopularProducts(builder.build());
            }
            if (builder instanceof RecentlyViewedProductsBuilder) {
                result = await this.recommender.recentlyViewedProducts(builder.build());
            }
            if (builder instanceof SimilarProductsProductBuilder) {
                result = await this.recommender.recommendSimilarProducts(builder.build());
            }
            if (builder instanceof PurchasedWithProductBuilder) {
                result = await this.recommender.recommendPurchasedWithProduct(builder.build());
            }
            if (builder instanceof ProductsViewedAfterViewingProductBuilder) {
                result = await this.recommender.recommendProductsViewedAfterViewingProduct(builder.build());
            }

            return (result?.recommendations as RelewiseProductResult[]) ?? undefined;
        } catch (err) {
            console.error('[Relewise Recommendation Service] Product recommendation request failed\n', err);
            return [];
        }
    }
}

export const relewiseRecommendationService = new RelewiseRecommendationService();
